import { useCallback } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiCodeTags, mdiFileTree, mdiLoading } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'

interface PropertyDerivationsProps {
  org: string
  property: { code: string; name?: Record<string, string> | null }
}

// The derivations computing a property, from its edit page (spec
// 2026-10-06-17): a link to each, or, when none computes it, the two ways to
// start one. Creating a derivation names it after the property, targets it,
// and opens it in the editor.
export function PropertyDerivations({ org, property }: PropertyDerivationsProps) {
  const { t } = useTranslation('properties')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canWriteConfiguration = useCan('configuration.write')

  const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/derivations', {
    params: { path: { orgCode: org }, query: { target_property_code: property.code, limit: 50 } as any },
  })
  const derivations = ((data as any)?.data || []) as Array<{ uid: string; code: string; name?: Record<string, string>; kind: string }>

  const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/derivations', {
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          return key[0] === 'get' && typeof key[1] === 'string' && key[1].includes('/derivations')
        },
      })
      navigate({ to: '/$org/derivations/$uid', params: { org, uid: created.uid } })
    },
  })

  const start = useCallback((kind: 'script' | 'table') => {
    const name = property.name || {}
    create.mutate({
      params: { path: { orgCode: org } },
      body: {
        code: `compute_${property.code}`,
        name: { en: name.en || property.code, fr: name.fr || name.en || property.code },
        target_property_code: property.code,
        ...(kind === 'script' ? { script: 'return null;', inputs: [] } : { decision_table: [] }),
      } as any,
    })
  }, [create, org, property])

  if (isLoading) {
    return <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin text-muted-foreground" />
  }

  if (derivations.length > 0) {
    return (
      <div className="text-sm" data-testid="property-derivations">
        <span className="text-muted-foreground">{t('edit.derived_by')} </span>
        {derivations.map((d, i) => (
          <span key={d.uid}>
            {i > 0 && ', '}
            <Link
              to="/$org/derivations/$uid"
              params={{ org, uid: d.uid }}
              className="underline font-medium"
              data-testid={`property-derivation-link-${d.code}`}
            >
              {d.name?.en || d.code}
            </Link>
          </span>
        ))}
      </div>
    )
  }

  const createError = create.error as any
  return (
    <div className="space-y-2" data-testid="property-derivations">
      <p className="text-sm text-muted-foreground">{t('edit.not_computed')}</p>
      {canWriteConfiguration && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => start('script')} disabled={create.isPending} data-testid="compute-with-formula-btn">
            <AppIcon path={mdiCodeTags} size="sm" className="mr-1" />
            {t('edit.compute_with_formula')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => start('table')} disabled={create.isPending} data-testid="compute-with-table-btn">
            <AppIcon path={mdiFileTree} size="sm" className="mr-1" />
            {t('edit.compute_with_table')}
          </Button>
        </div>
      )}
      {createError && (
        <p className="text-sm text-destructive" data-testid="compute-create-error">
          {t('edit.compute_error')}: {createError.title || createError.detail || ''}
        </p>
      )}
    </div>
  )
}
